/**
 * Gating rules for the payment-history row actions. The Undo2 button covers two unrelated
 * operations: refunding a payment that moved money, and cancelling a scheduled payment not yet sent
 * to the processor. ACH refunds must go through Support, but a PENDING SCHEDULED ACH payment has no
 * processor transaction — cancelling only flips the row to CANCELLED — so the ACH restriction must
 * NOT reach the cancel case. A logged payment is voided instead; the backend does the enforcing.
 */

export interface PaymentRowSource {
  payment_type?: string
}

export interface PaymentRowPayment {
  status?: string
  /** PARTIALLY_REFUNDED while a REFUND payment still has money on a card. */
  display_status?: string
  payment_origin?: string
  processed_time?: string | null
  sources?: PaymentRowSource[]
}

export interface PaymentRow {
  payment?: PaymentRowPayment
  payment_method?: string
  /** Display label for the origin column, e.g. 'Scheduled', 'BlytzCash™'. */
  payment_origin?: string
}

const REFUND_BLOCKING_STATUSES = ['REFUND', 'FAILED', 'CANCELLED']

export function hasAchSource(row: PaymentRow): boolean {
  return (
    row?.payment?.sources?.some(source => source.payment_type === 'ACH') ===
    true
  )
}

/**
 * True when the row is a scheduled payment that has not been processed yet,
 * i.e. the row the button cancels rather than refunds. Mirrors the backend
 * guard on `cancel_scheduled_payment`: PENDING + SCHEDULED + no processed_time.
 */
export function isCancellableScheduledPayment(row: PaymentRow): boolean {
  return (
    row?.payment?.status?.toUpperCase() === 'PENDING' &&
    row?.payment?.payment_origin === 'SCHEDULED' &&
    !row?.payment?.processed_time
  )
}

/** True when the refund/cancel button should be disabled for this row. */
export function isRefundOrCancelDisabled(row: PaymentRow): boolean {
  // A REFUND or CANCELLED payment still holding money on a card can refund the rest.
  const partlyRefunded = row?.payment?.display_status === 'PARTIALLY_REFUNDED'

  return (
    (REFUND_BLOCKING_STATUSES.includes(
      row?.payment?.status?.toUpperCase() ?? ''
    ) &&
      !partlyRefunded) ||
    row?.payment?.status == null ||
    row?.payment_method === 'Cash' ||
    (hasAchSource(row) && !isCancellableScheduledPayment(row)) ||
    row?.payment_origin === 'BlytzCash™' ||
    // A logged payment (cash/check/money order over the counter) has no processor transaction, so
    // nothing is refundable through software. Gate on the origin, NOT payment_method: a Check or Money
    // Order tender's payment_method is not the literal 'Cash' and would slip past the check above.
    row?.payment_origin === 'Logged'
  )
}

/**
 * Tooltip shown while the button is disabled. ACH refunds are the only case
 * with an explanation to give; everything else stays untitled as before.
 */
export function refundOrCancelDisabledTitle(row: PaymentRow): string | null {
  return hasAchSource(row) && !isCancellableScheduledPayment(row)
    ? 'Please contact Support for assistance.'
    : null
}

/**
 * True for a logged payment (cash/check/money order taken over the
 * counter). These rows get a Void button in place of Refund.
 */
export function isLoggedPayment(row: PaymentRow): boolean {
  return row?.payment_origin === 'Logged'
}

/**
 * True when the void button should be disabled. Only a PAID logged payment can be voided -- one that
 * has already been voided, cancelled, or otherwise moved off PAID has nothing left to correct through
 * this action. The other refusal reasons (not today, not the account's most recent payment, invoice
 * already touched) live on the backend and surface as a 4xx message instead of a disabled button,
 * since the client doesn't have enough information to pre-check them.
 */
export function isVoidDisabled(row: PaymentRow): boolean {
  return row?.payment?.status?.toUpperCase() !== 'PAID'
}

/** Tooltip shown while the void button is disabled. */
export function voidDisabledTitle(): string {
  return 'Only a paid logged payment can be voided.'
}

/** Tenders with no processor transaction behind them: nothing to reverse. */
const NON_REFUNDABLE_TRANSACTION_TYPES = ['CASH', 'CHECK', 'MONEY_ORDER']

/** Transaction states with nothing to send back. */
const NON_REFUNDABLE_TRANSACTION_STATUSES = ['DECLINED', 'PENDING', 'CANCELLED']

export interface ReportTransaction {
  type?: string
  status?: string
  /** Serializer's own verdict, computed before the status is blanked. */
  is_refundable?: boolean
}

/**
 * Whether the Payment Report's per-source refund button may be shown, on the tender and transaction
 * state alone. The answer comes from the API as `is_refundable`, computed from the real status before
 * any blanking, so a display change can no longer move a money button. The local derivation survives
 * only as the fallback for an API that predates the field: this repo and blytzpay-django deploy
 * separately, and defaulting a refund button to visible is the wrong way to lose that race.
 */
export function canRefundSource(transaction?: ReportTransaction): boolean {
  if (!transaction) return false
  if (typeof transaction.is_refundable === 'boolean') {
    return transaction.is_refundable
  }

  return !(
    NON_REFUNDABLE_TRANSACTION_TYPES.includes(transaction.type ?? '') ||
    NON_REFUNDABLE_TRANSACTION_STATUSES.includes(transaction.status ?? '')
  )
}
